import { ReactNode, useEffect, useState } from 'react';
import { Clock, ShieldAlert, FileCheck2 } from 'lucide-react';
import { useUser } from '../../hooks/useUser';
import type { AccountStatus } from '../../types/profile';
import api from '../../services/api';

/**
 * Retient à la porte les comptes dont le dossier n'est pas encore validé.
 *
 * Règle métier : une institution (ou une entreprise) dont le profil n'a pas été
 * validé par le back-office ne circule pas dans l'application, elle reste en
 * attente. Jusqu'ici rien ne l'appliquait : `ProtectedRoute` ne vérifiait que
 * l'authentification, si bien qu'un compte en attente entrait comme un autre.
 *
 * Ce qui reste TOUJOURS accessible, même en attente, et donc n'est pas placé
 * sous cette garde dans le routeur :
 *  - le profil et les formulaires entreprise/institution, puisque c'est
 *    précisément ce que l'on attend du client ;
 *  - l'abonnement, parce qu'un dossier en attente doit pouvoir souscrire.
 */

/** Statuts qui autorisent l'usage normal de l'application. */
const STATUTS_OUVERTS: AccountStatus[] = ['active'];

/** Statuts qui signifient « dossier en cours d'examen ». */
const STATUTS_EN_ATTENTE: AccountStatus[] = [
  'pending',
  'needs_validation',
  'validation_in_progress',
];

export function isAccountOpen(status?: AccountStatus): boolean {
  // Statut inconnu : on laisse passer. Un backend plus ancien, ou une réponse
  // incomplète, ne doit pas enfermer dehors des clients qui travaillent.
  return !status || STATUTS_OUVERTS.includes(status);
}

export function isAccountPending(status?: AccountStatus): boolean {
  return !!status && STATUTS_EN_ATTENTE.includes(status);
}

interface AccountStatusGateProps {
  children: ReactNode;
}

/** Coordonnées du support, configurées dans le back-office. */
interface SupportContacts {
  email: string;
  phone?: string | null;
  whatsapp?: string | null;
}

const SUPPORT_FALLBACK: SupportContacts = { email: 'ikiota@ikiotahub.com' };

/**
 * Va chercher les coordonnées du support.
 *
 * En cas d'échec, on garde l'adresse de repli : un écran qui annonce un
 * problème sans dire à qui s'adresser n'aide personne.
 */
function useSupportContacts(): SupportContacts {
  const [contacts, setContacts] = useState<SupportContacts>(SUPPORT_FALLBACK);

  useEffect(() => {
    let vivant = true;
    api
      .get<{ data?: SupportContacts } & SupportContacts>('/support-contacts')
      .then((res) => {
        if (!vivant) return;
        const data = (res as { data?: SupportContacts })?.data ?? (res as SupportContacts);
        if (data?.email) setContacts(data);
      })
      .catch(() => {
        // Repli déjà en place.
      });
    return () => {
      vivant = false;
    };
  }, []);

  return contacts;
}

export function AccountStatusGate({ children }: AccountStatusGateProps) {
  const { user, isLoading } = useUser();
  const support = useSupportContacts();
  const status = user?.accountStatus;
  const reason = user?.accountSuspensionReason;
  // Page de profil à proposer : celle qui correspond au type de compte.
  const profilePath =
    user?.userType === 'financial_institution'
      ? '/financial-institution'
      : '/company';

  // Tant que `/users/me` n'a pas répondu, le statut est inconnu : ne pas
  // afficher un écran d'attente qui disparaîtrait une seconde plus tard.
  if (isLoading || isAccountOpen(status)) {
    return <>{children}</>;
  }

  if (isAccountPending(status)) {
    return (
      <StatusScreen
        icon={<Clock className="w-12 h-12 text-[#197ca8]" />}
        title="Votre dossier est en cours de validation"
        message={
          "Nos équipes vérifient les informations et les documents de votre profil. " +
          "Vous recevrez un courriel dès que votre compte sera activé."
        }
        hint="Vous pouvez compléter ou corriger votre profil en attendant."
        actionLabel="Compléter mon profil"
        actionHref={profilePath}
      />
    );
  }

  if (status === 'suspended') {
    return (
      <StatusScreen
        icon={<ShieldAlert className="w-12 h-12 text-red-600" />}
        title="Votre compte est suspendu"
        message={
          reason
            ? `Motif : ${reason}`
            : "L'accès à votre compte a été suspendu par nos équipes."
        }
        hint={supportHint(support)}
        actionLabel="Écrire au support"
        actionHref={`mailto:${support.email}`}
      />
    );
  }

  // inactive, ou tout statut futur non prévu ici.
  return (
    <StatusScreen
      icon={<FileCheck2 className="w-12 h-12 text-gray-500" />}
      title="Votre compte n'est pas actif"
      message="L'accès à l'application n'est pas ouvert pour ce compte."
      hint={supportHint(support)}
      actionLabel="Écrire au support"
      actionHref={`mailto:${support.email}`}
    />
  );
}

/**
 * Bandeau d'information, pour les pages qui restent accessibles en attente.
 * Le client doit savoir où en est son dossier là où il le complète, sans avoir
 * à deviner pourquoi le reste de l'application lui est fermé.
 */
export function AccountStatusNotice() {
  const { user, isLoading } = useUser();
  const status = user?.accountStatus;

  if (isLoading || isAccountOpen(status)) return null;

  if (isAccountPending(status)) {
    return (
      <div className="mb-4 flex items-start gap-3 rounded-lg border border-[#197ca8]/30 bg-[#197ca8]/5 p-4">
        <Clock className="mt-0.5 h-5 w-5 flex-shrink-0 text-[#197ca8]" />
        <div className="text-sm">
          <p className="font-medium text-gray-900">
            Dossier en cours de validation
          </p>
          <p className="text-gray-600">
            Vous pouvez compléter ou corriger vos informations. L'accès complet
            à la plateforme sera ouvert dès la validation par nos équipes, et
            vous en serez averti par courriel.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="mb-4 flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 p-4">
      <ShieldAlert className="mt-0.5 h-5 w-5 flex-shrink-0 text-red-600" />
      <div className="text-sm">
        <p className="font-medium text-gray-900">Compte non actif</p>
        <p className="text-gray-600">
          {user?.accountSuspensionReason
            ? `Motif : ${user.accountSuspensionReason}`
            : "L'accès à la plateforme est fermé pour ce compte. Contactez le support."}
        </p>
      </div>
    </div>
  );
}

/** Une ligne de contact, sobre : l'adresse, et le WhatsApp s'il existe. */
function supportHint(support: SupportContacts): string {
  const phone = support.whatsapp || support.phone;
  return phone ? `Support : ${support.email}, ${phone}` : `Support : ${support.email}`;
}

function StatusScreen({
  icon,
  title,
  message,
  hint,
  actionLabel,
  actionHref,
}: {
  icon: ReactNode;
  title: string;
  message: string;
  hint: string;
  actionLabel: string;
  actionHref: string;
}) {
  return (
    <div className="min-h-[60vh] flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
        <div className="flex justify-center mb-4">{icon}</div>
        <h2 className="text-xl font-semibold text-gray-900 mb-3">{title}</h2>
        <p className="text-gray-600 mb-4">{message}</p>
        <p className="text-sm text-gray-500">{hint}</p>
        <a
          href={actionHref}
          className="inline-block mt-6 px-6 py-2 bg-[#197ca8] text-white rounded-md hover:bg-[#156a92] transition-colors"
        >
          {actionLabel}
        </a>
      </div>
    </div>
  );
}
